import * as React from 'react';
import {Glyph} from '../../types/Glyph';
import {MouseEventHandler, ReactNode, useMemo} from 'react';
import styles from './Icon.module.css';
import RenderHelpers from '../../../shared-ui/helpers/RenderHelpers';
import Helpers from '../../../shared-common/helpers/Helpers';

import {ReactComponent as AcceptGlyph} from './glyphs/accept.svg';
import {ReactComponent as AddGlyph} from './glyphs/add.svg';
import {ReactComponent as BackFilledGlyph} from './glyphs/back-filled.svg';
import {ReactComponent as BackGlyph} from './glyphs/back.svg';
import {ReactComponent as BusyGlyph} from './glyphs/busy.svg';
import {ReactComponent as CancelGlyph} from './glyphs/cancel.svg';
import {ReactComponent as CollapseGlyph} from './glyphs/collapse.svg';
import {ReactComponent as DeleteFilledGlyph} from './glyphs/delete-filled.svg';
import {ReactComponent as DeleteGlyph} from './glyphs/delete.svg';
import {ReactComponent as DraggableGlyph} from './glyphs/draggable.svg';
import {ReactComponent as ExpandGlyph} from './glyphs/expand.svg';
import {ReactComponent as ForwardFilledGlyph} from './glyphs/forward-filled.svg';
import {ReactComponent as ForwardGlyph} from './glyphs/forward.svg';
import {ReactComponent as PasteGlyph} from './glyphs/paste.svg';
import {ReactComponent as SaveGlyph} from './glyphs/save.svg';
import {ReactComponent as SubmitFilledGlyph} from './glyphs/submit-filled.svg';
import {ReactComponent as SubmitGlyph} from './glyphs/submit.svg';
import {ReactComponent as AiGlyph} from './glyphs/ai.svg';
import {ReactComponent as SadGlyph} from './glyphs/sad.svg';
import {ReactComponent as HappyGlyph} from './glyphs/happy.svg';
import {GlyphStyle} from '../../types/GlyphStyle';

export interface IconProps {
  className?: string;
  style?: React.CSSProperties;
  glyph: Glyph | ReactNode;
  glyphStyle?: GlyphStyle;
  size?: number | string;
  color?: string;
  onMouseOver?: MouseEventHandler<HTMLDivElement>;
  onMouseOut?: MouseEventHandler<HTMLDivElement>;
}

export const Icon = (props: IconProps) => {
  const contents = useMemo(
    () => {
      if (typeof props.glyph !== 'string') {
        return props.glyph;
      }

      const glyphStyle = Helpers.ifNil(props.glyphStyle, 'outline');
      const isFilled = glyphStyle === 'solid';

      switch (props.glyph) {
        case 'accept':
          return <AcceptGlyph/>;
        case 'add':
          return <AddGlyph/>;
        case 'back':
          return isFilled ? <BackFilledGlyph/> : <BackGlyph/>;
        case 'busy':
          return <BusyGlyph/>;
        case 'cancel':
          return <CancelGlyph/>;
        case 'collapse':
          return <CollapseGlyph/>;
        case 'delete':
          return isFilled ? <DeleteFilledGlyph/> : <DeleteGlyph/>;
        case 'draggable':
          return <DraggableGlyph/>;
        case 'expand':
          return <ExpandGlyph/>;
        case 'forward':
          return isFilled ? <ForwardFilledGlyph/> : <ForwardGlyph/>;
        case 'paste':
          return <PasteGlyph/>;
        case 'save':
          return <SaveGlyph/>;
        case 'submit':
          return isFilled ? <SubmitFilledGlyph/> : <SubmitGlyph/>;
        case 'ai':
          return <AiGlyph/>;
        case 'sad':
          return <SadGlyph/>;
        case 'happy':
          return <HappyGlyph/>;
        default:
          return null;
      }
    },
    [props.glyph, props.glyphStyle]
  );

  const isAnimated = props.glyph === 'busy';

  return (
    <div
      className={
        RenderHelpers.classes(
          styles.component,
          props.className,
          isAnimated ? styles.animated : null
        )
      }
      style={{
        fill: props.color,
        ...props.style,
        ...{'--icon-size': `${Helpers.ifNil(props.size, 16)}px`},
      }}
      onMouseOver={props.onMouseOver}
      onMouseOut={props.onMouseOut}
    >
      {contents}
    </div>
  );
};